// src/encounter/lib/answer-set.ts
// QuestionBank answer sets: one encounter can hold several QuestionnaireResponses, the primary one (the picked template)
// and any number of inserted ones (a template added from the "/" menu). Each set has a stable id (the `identifier`)
// that stays the same across the new rows a save after a sign makes.
import { ApiError } from '@/shared/api/api-client';
import type { QuestionnaireResponseResource } from '@/types/questionnaire-response-save-api';

export const ANSWER_SET_SYSTEM = 'https://fhir.scrips.com/answer-set';
export const ANSWER_SET_ROLE_URL = 'https://scrips.com/fhir/StructureDefinition/answer-set-role';

/** The answer set id a QR carries, or undefined when it carries none. */
export function answerSetIdOf(qr: QuestionnaireResponseResource | null | undefined): string | undefined {
  const idf = qr?.identifier;
  return idf?.system === ANSWER_SET_SYSTEM && idf.value ? idf.value : undefined;
}

/** True for a set added from the "/" menu (the role extension says `inserted`); the primary set carries none. */
export function isInsertedAnswerSet(qr: QuestionnaireResponseResource | null | undefined): boolean {
  return (qr?.extension ?? []).some((e) => e.url === ANSWER_SET_ROLE_URL && e.valueCode === 'inserted');
}

/** The extension that marks a set as inserted. */
export function insertedRoleExtension() {
  return { url: ANSWER_SET_ROLE_URL, valueCode: 'inserted' as const };
}

/** Oldest first: the order the sets were added in. */
export function byAuthoredAscending(a: QuestionnaireResponseResource, b: QuestionnaireResponseResource): number {
  const ta = a.authored ? Date.parse(a.authored) : 0;
  const tb = b.authored ? Date.parse(b.authored) : 0;
  return ta - tb;
}

/** The `If-Match` value for a stored version id (a weak ETag). */
export function ifMatchOf(versionId: string): string {
  return `W/"${versionId}"`;
}

/** The QuestionBank refusal code of a 409, or null for anything else. */
export function readConflictCode(error: unknown): { code: string; currentVersion?: string } | null {
  if (!(error instanceof ApiError) || error.status !== 409) return null;
  try {
    const body = JSON.parse(error.body) as { code?: unknown; currentVersion?: unknown } | null;
    if (!body || typeof body.code !== 'string') return null;
    return { code: body.code, ...(body.currentVersion != null ? { currentVersion: String(body.currentVersion) } : {}) };
  } catch {
    return null; // not JSON: not QuestionBank's refusal
  }
}

/**
 * A 409 `version-conflict` or `not-current-revision`: another writer moved the row on. Carries the code and the version the
 * row stands at now when the server says it; null for any other failure. After `not-current-revision` the set's head is a
 * DIFFERENT row (a save after a sign makes one), so the caller reads the head again for its id.
 */
export function readVersionConflict(
  error: unknown,
): { code: 'version-conflict' | 'not-current-revision'; currentVersion?: string } | null {
  const c = readConflictCode(error);
  if (c?.code !== 'version-conflict' && c?.code !== 'not-current-revision') return null;
  return c.currentVersion ? { code: c.code, currentVersion: c.currentVersion } : { code: c.code };
}
